import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Icon } from '@iconify/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/shadcn/dialog';
import { Button } from '@/components/shadcn/button';

export interface ImageCropModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageFile: File | null;
  type: 'logo' | 'cover';
  onCropComplete: (croppedFile: File) => Promise<void>;
}

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
  isOpen,
  onClose,
  imageFile,
  type,
  onCropComplete,
}) => {
  const [imageObj, setImageObj] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState<number>(1);
  const [rotation, setRotation] = useState<number>(0);
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Target dimensions based on type
  const targetWidth = type === 'logo' ? 1024 : 1200;
  const targetHeight = type === 'logo' ? 1024 : 300;
  const aspectRatio = targetWidth / targetHeight;

  // Load image object when imageFile changes
  useEffect(() => {
    if (!imageFile) {
      setImageObj(null);
      return;
    }

    const url = URL.createObjectURL(imageFile);
    const img = new Image();
    img.onload = () => {
      setImageObj(img);
      setZoom(1);
      setRotation(0);
      setPan({ x: 0, y: 0 });
    };
    img.src = url;

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [imageFile]);

  // Redraw preview canvas
  const drawPreview = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageObj) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    ctx.save();
    ctx.translate(canvasWidth / 2 + pan.x, canvasHeight / 2 + pan.y);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(zoom, zoom);

    // Calculate image draw size maintaining aspect ratio
    const imgRatio = imageObj.width / imageObj.height;
    let drawW = canvasWidth;
    let drawH = canvasHeight;

    if (imgRatio > aspectRatio) {
      drawH = canvasHeight;
      drawW = canvasHeight * imgRatio;
    } else {
      drawW = canvasWidth;
      drawH = canvasWidth / imgRatio;
    }

    ctx.drawImage(imageObj, -drawW / 2, -drawH / 2, drawW, drawH);
    ctx.restore();
  }, [imageObj, zoom, rotation, pan, aspectRatio]);

  useEffect(() => {
    drawPreview();
  }, [drawPreview]);

  // Pan handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setPan({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch handlers for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      const touch = e.touches[0];
      setDragStart({ x: touch.clientX - pan.x, y: touch.clientY - pan.y });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || e.touches.length !== 1) return;
    const touch = e.touches[0];
    setPan({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y,
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Export cropped file
  const handleSave = async () => {
    if (!imageObj) return;

    setIsSubmitting(true);
    try {
      // Offscreen canvas at target resolution
      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = targetWidth;
      exportCanvas.height = targetHeight;
      const ctx = exportCanvas.getContext('2d');

      if (!ctx) throw new Error('Canvas context failed');

      // Draw background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, targetWidth, targetHeight);

      ctx.save();
      ctx.translate(targetWidth / 2 + (pan.x * (targetWidth / 320)), targetHeight / 2 + (pan.y * (targetHeight / 320)));
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(zoom, zoom);

      const imgRatio = imageObj.width / imageObj.height;
      let drawW = targetWidth;
      let drawH = targetHeight;

      if (imgRatio > aspectRatio) {
        drawH = targetHeight;
        drawW = targetHeight * imgRatio;
      } else {
        drawW = targetWidth;
        drawH = targetWidth / imgRatio;
      }

      ctx.drawImage(imageObj, -drawW / 2, -drawH / 2, drawW, drawH);
      ctx.restore();

      // Convert canvas to Blob / File
      exportCanvas.toBlob(
        async (blob) => {
          if (!blob) {
            setIsSubmitting(false);
            return;
          }
          const fileName = type === 'logo' ? 'community-logo.webp' : 'community-cover.webp';
          const croppedFile = new File([blob], fileName, { type: 'image/webp' });

          await onCropComplete(croppedFile);
          setIsSubmitting(false);
          onClose();
        },
        'image/webp',
        0.92
      );
    } catch (err) {
      console.error('Cropping error:', err);
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md rounded-3xl p-6 bg-white overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-lg font-extrabold text-[#0E1726] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon icon="ph:crop-bold" className="w-5 h-5 text-[#FC480D]" />
              <span>{type === 'logo' ? 'Edit & Potong Logo' : 'Edit & Potong Sampul'}</span>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Cropper Box Viewport */}
        <div className="space-y-4 my-2">
          <div
            ref={containerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            className="relative w-full bg-slate-950 rounded-2xl overflow-hidden cursor-move flex items-center justify-center select-none shadow-inner border border-gray-200"
            style={{
              aspectRatio: type === 'logo' ? '1/1' : '4/1',
              maxHeight: type === 'logo' ? '300px' : '200px',
            }}
          >
            <canvas
              ref={canvasRef}
              width={type === 'logo' ? 320 : 360}
              height={type === 'logo' ? 320 : 90}
              className="w-full h-full object-contain pointer-events-none"
            />

            {/* Overlay Crop Mask */}
            <div className="absolute inset-0 border-2 border-[#FC480D]/80 rounded-2xl pointer-events-none shadow-[0_0_0_9999px_rgba(0,0,0,0.4)] flex items-center justify-center">
              {type === 'logo' && (
                <div className="w-full h-full rounded-full border border-white/60 border-dashed" />
              )}
            </div>
          </div>

          {/* Controls: Zoom & Rotate */}
          <div className="space-y-3 bg-[#F8FAFC] p-3.5 rounded-2xl border border-gray-150">
            {/* Zoom Slider */}
            <div className="flex items-center gap-3">
              <Icon icon="ph:magnifying-glass-minus-bold" className="w-4 h-4 text-gray-400" />
              <input
                type="range"
                min="0.5"
                max="3"
                step="0.05"
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#FC480D]"
              />
              <Icon icon="ph:magnifying-glass-plus-bold" className="w-4 h-4 text-gray-400" />
            </div>

            {/* Action Buttons: Rotate & Reset */}
            <div className="flex items-center justify-between text-xs pt-1">
              <button
                type="button"
                onClick={() => setRotation((prev) => (prev + 90) % 360)}
                className="flex items-center gap-1.5 font-bold text-gray-700 hover:text-[#FC480D] transition-colors cursor-pointer"
              >
                <Icon icon="ph:arrow-clockwise-bold" className="w-4 h-4" />
                <span>Putar 90°</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setZoom(1);
                  setRotation(0);
                  setPan({ x: 0, y: 0 });
                }}
                className="font-bold text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        <DialogFooter className="flex items-center justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-xl text-xs font-bold"
          >
            Batal
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={isSubmitting}
            className="bg-[#FC480D] hover:bg-[#e03e09] text-white rounded-xl text-xs font-bold gap-1.5 shadow-sm"
          >
            {isSubmitting ? (
              <>
                <Icon icon="ph:spinner-gap-bold" className="w-4 h-4 animate-spin" />
                <span>Memproses...</span>
              </>
            ) : (
              <>
                <Icon icon="ph:check-bold" className="w-4 h-4" />
                <span>Gunakan Gambar</span>
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropModal;
